import React, { useState, useEffect, useContext } from "react"
import {
  Button,
  Col,
  Form,
  Input,
  Label,
  Row,
  UncontrolledAlert,
  Card,
  CardBody,
  Modal,
} from "reactstrap"
import TenantConnectionContext from "./TenantConnectionContext"
import { OUTBOUND_MODULE_NAMES } from "./TenantConnectionDetails"
import LoadingOverlay from "react-loading-overlay"
import Error from "pages/NotificationModal/Error"
import Success from "./Success"
import Confirmation from "./Confirmation"

// BROADCAST is a synthesized value (multiple channels selected at once),
// never a selectable option itself.
const channelOptions = Object.entries(OUTBOUND_MODULE_NAMES)
  .filter(([value]) => value !== "BROADCAST_OB_STS_UPD_Q")
  .map(([value, label]) => ({ value, label }))

const TenantIntegrationForm = ({ hideForm }) => {
  const {
    loading,
    success,
    error,
    setSuccess,
    setError,
    integrationData,
    submitIntegrationUpdate,
    integrationUpdateResponse,
  } = useContext(TenantConnectionContext)
  const [showConfirmation, setShowConfirmation] = useState(false)

  const [formData, setFormData] = useState({ selectedChannels: [] })
  const [errorMsg, setErrorMsg] = useState("")

  useEffect(() => {
    if (integrationData) {
      const currentChannel = integrationData.tms_update_sts_channel
      let selectedChannels = []

      if (currentChannel === "BROADCAST_OB_STS_UPD_Q") {
        try {
          const config = JSON.parse(integrationData.config.replace(/'/g, '"'))
          selectedChannels = config.tms_update_sts_channels || []
        } catch (e) {
          selectedChannels = []
        }
      } else if (currentChannel) {
        const isInOptions = channelOptions.some(
          option => option.value === currentChannel,
        )
        if (isInOptions) {
          selectedChannels = [currentChannel]
        }
      }

      setFormData({ selectedChannels })
    }
  }, [integrationData])

  const handleChannelChange = e => {
    const { value, checked } = e.target

    setFormData(prev => ({
      ...prev,
      selectedChannels: checked
        ? [...prev.selectedChannels, value]
        : prev.selectedChannels.filter(channel => channel !== value),
    }))
  }

  const handleSubmit = () => {
    if (formData.selectedChannels.length === 0) {
      setErrorMsg("Please select at least one channel")
      return
    }
    setShowConfirmation(true)
  }

  const handleConfirmSubmit = async () => {
    setShowConfirmation(false)
    try {
      const payload = { tms_update_sts_channel: formData.selectedChannels }
      await submitIntegrationUpdate(payload)
      setErrorMsg("")
    } catch (err) {
      setErrorMsg(`Error: ${err.message}`)
    }
  }

  const handleCancel = () => {
    setShowConfirmation(false)
    hideForm()
  }

  const closeSuccess = () => {
    setSuccess(false)
    hideForm()
  }

  const togError = () => setError(false)

  if (!integrationData) {
    return (
      <LoadingOverlay active={loading} spinner>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <h5>Loading...</h5>
        </div>
      </LoadingOverlay>
    )
  }

  return (
    <>
      <Modal isOpen={success} toggle={closeSuccess} centered size="md">
        <Success data={integrationUpdateResponse} cancel={closeSuccess} />
      </Modal>

      <Modal isOpen={error} toggle={togError} centered size="sm">
        <Error message="Something went wrong" closeModal={togError} />
      </Modal>

      <Modal
        isOpen={showConfirmation}
        toggle={() => setShowConfirmation(false)}
        centered
        size="sm"
      >
        <Confirmation
          message="Do you want to save these changes?"
          confirm={handleConfirmSubmit}
          cancel={() => setShowConfirmation(false)}
        />
      </Modal>

      <LoadingOverlay active={loading} spinner>
        <Card className="p-3 mt-3">
          <div className="mb-3">
            <h6 className="text-primary">Edit Tenant Outbound Integration</h6>
          </div>
          <CardBody className="p-0">
            <Card outline color="primary" className="border mt-2 mb-3">
              <CardBody className="p-3">
                {errorMsg && (
                  <div className="mb-3">
                    <UncontrolledAlert color="danger" role="alert">
                      {errorMsg}
                    </UncontrolledAlert>
                  </div>
                )}

                <Form>
                  <Row className="mb-2">
                    <Label className="col-sm-3 col-form-label">
                      TMS Update Status Channels *
                    </Label>
                    <Col sm={6} className="ms-4">
                      <div>
                        {channelOptions.map(option => (
                          <div key={option.value} className="form-check mb-2">
                            <Input
                              type="checkbox"
                              className="form-check-input"
                              id={option.value}
                              value={option.value}
                              checked={formData.selectedChannels.includes(
                                option.value,
                              )}
                              onChange={handleChannelChange}
                            />
                            <Label
                              className="form-check-label"
                              htmlFor={option.value}
                            >
                              {option.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>

            <div className="d-flex justify-content-end mt-3 me-4">
              <div className="d-flex gap-2">
                <Button onClick={handleSubmit} className="btn" color="primary">
                  Save
                </Button>
                <Button
                  onClick={handleCancel}
                  className="btn"
                  color="secondary"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </LoadingOverlay>
    </>
  )
}

export default TenantIntegrationForm
